import { Typography } from '@mui/material';

import { neutrals } from '@toolbox/style/theme/common/ThemeProvider/colors';
import Button from '../Button';
import Checkbox from '../Checkbox';
import Icon from '../Icon';
import { checkboxSx, toggleButtonSx } from './styles';

import type { SxProps, Theme } from '@mui/material';
import type { Variant } from '@mui/material/styles/createTypography';
import type * as React from 'react';
import type { ButtonProps } from '../Button/Button';
import type { IconName } from '../Icon/Icon';

export interface ToggleButtonProps extends Omit<ButtonProps, 'onClick' | 'size' | 'sx'> {
  /** Overriding styles for ToggleButton. */
  sx?: SxProps<Theme>;
  /** Overriding styles for Checkbox. */
  boxSx?: SxProps<Theme>;
  /** Fired when user selects ToggleButton. */
  onCheck?: () => void;
  /** If `true`, means that ToggleButton is selected. */
  selected: boolean;
  /** Labeled text of ToggleButton. */
  label: string;
  /** Icon to display. Will be hidden if it is not defined. */
  icon?: IconName;
  /** Size of icon in pixels. */
  size?: number;
  /** Typography variant for label display. */
  labelVariant?: Variant;
  /** If `true`, checkbox in the top right corner will be hidden. */
  hideCheckbox?: boolean;
}

const ToggleButton: React.FC<ToggleButtonProps> = ({
  sx,
  boxSx,
  selected,
  label,
  labelVariant = 'body2',
  size = 48,
  icon,
  hideCheckbox = false,
  onCheck,
  ...other
}) => {
  return (
    <Button onClick={onCheck} sx={toggleButtonSx(selected, sx)} {...(other as Theme)}>
      {icon && <Icon sx={{ width: size, height: size }} icon={icon} />}
      <Typography sx={() => ({ marginTop: icon ? 1 : 0, color: neutrals.dark })}>
        {label}
      </Typography>
      {!hideCheckbox && <Checkbox sx={checkboxSx(boxSx)} checked={selected} />}
    </Button>
  );
};

export default ToggleButton;
